<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import axios from 'axios'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()

const uuid = ref(route.params.uuid as string)
const hash = ref(route.params.hash as string)

const loading = ref(true)
const error = ref<string | null>(null)
const clientToken = ref<string | null>(null)
const sessionId = ref<string | null>(null)
const htmlSnippet = ref<string | null>(null)
const usePaymentsAPI = ref(false)

// Fetch Klarna data from backend
const fetchKlarnaData = async () => {
  loading.value = true
  error.value = null

  try {
    const response = await axios.get(
      `${import.meta.env.VITE_API_URL}/v1/payments-online/${uuid.value}/${hash.value}/klarna-snippet`
    )

    clientToken.value = response.data.client_token
    sessionId.value = response.data.session_id
    htmlSnippet.value = response.data.html_snippet

    // Determine which API is being used
    usePaymentsAPI.value = !!clientToken.value

    // Stop loading to show the container
    loading.value = false

    // Wait for Vue to update the DOM
    await nextTick()

    // Extra wait to ensure DOM is fully rendered
    await new Promise(resolve => setTimeout(resolve, 200))

    console.log('DOM should be ready, loading widget...')
    console.log('Container exists:', !!document.getElementById('klarna-payments-container'))

    if (usePaymentsAPI.value) {
      // Use Klarna Payments API
      await loadKlarnaPaymentsWidget()
    } else if (htmlSnippet.value) {
      // Use Klarna Checkout API
      await loadKlarnaCheckoutSnippet()
    }
  } catch (err: any) {
    console.error('Error fetching Klarna data:', err)
    error.value = err.response?.data?.message || t('klarnaCheckout.klarnaLoadError')
    loading.value = false
  }
}

// Load Klarna Payments API widget
const loadKlarnaPaymentsWidget = async () => {
  try {
    console.log('Loading Klarna Payments widget...')
    console.log('Client token:', clientToken.value?.substring(0, 50) + '...')

    // Load Klarna SDK
    const script = document.createElement('script')
    script.src = 'https://x.klarnacdn.net/kp/lib/v1/api.js'
    script.async = true

    await new Promise((resolve, reject) => {
      script.onload = () => {
        console.log('Klarna SDK loaded successfully')
        resolve(true)
      }
      script.onerror = (err) => {
        console.error('Failed to load Klarna SDK:', err)
        reject(err)
      }
      document.head.appendChild(script)
    })

    // Wait for Klarna to be available
    let attempts = 0
    while (!(window as any).Klarna && attempts < 50) {
      await new Promise(resolve => setTimeout(resolve, 100))
      attempts++
    }

    if (!(window as any).Klarna) {
      throw new Error('Klarna SDK not available after loading')
    }

    console.log('Klarna SDK available, initializing...')

    // Initialize Klarna Payments
    if (clientToken.value) {
      const container = document.getElementById('klarna-payments-container')
      if (!container) {
        throw new Error('Klarna container not found in DOM')
      }

      console.log('Initializing Klarna Payments with client_token...')

      try {
        await (window as any).Klarna.Payments.init({
          client_token: clientToken.value
        })

        console.log('Klarna Payments initialized, loading payment method...')

        // Load payment method category - use 'pay_later' as it's available in the response
        ;(window as any).Klarna.Payments.load(
          {
            container: '#klarna-payments-container',
            payment_method_category: 'pay_later'
          },
          (res: any) => {
            console.log('Klarna Payments load callback:', res)

            // Check what's in the container
            const container = document.getElementById('klarna-payments-container')
            console.log('Container after load:', container)
            console.log('Container innerHTML length:', container?.innerHTML.length)
            console.log('Container has children:', container?.children.length)

            if (res.show_form === false) {
              console.error('Klarna refused to show form:', res)
              error.value = t('klarnaCheckout.klarnaFormError')
            } else {
              console.log('Klarna widget loaded successfully')

              // Monitor iframe height changes
              setTimeout(() => {
                const iframe = container?.querySelector('iframe')
                if (iframe) {
                  console.log('Iframe height after 2s:', iframe.style.height)
                  console.log('Iframe computed height:', window.getComputedStyle(iframe).height)
                }
              }, 2000)

              setTimeout(() => {
                const iframe = container?.querySelector('iframe')
                if (iframe) {
                  console.log('Iframe height after 5s:', iframe.style.height)
                }
              }, 5000)
            }
          }
        )
      } catch (initError) {
        console.error('Error during Klarna init or load:', initError)
        throw initError
      }
    }
  } catch (err) {
    console.error('Error loading Klarna Payments widget:', err)
    error.value = t('klarnaCheckout.klarnaWidgetError') + (err as Error).message
  }
}

// Load Klarna Checkout API snippet
const loadKlarnaCheckoutSnippet = async () => {
  const container = document.getElementById('klarna-checkout-container')
  if (container && htmlSnippet.value) {
    container.innerHTML = htmlSnippet.value

    // Execute any scripts in the snippet
    const scripts = container.getElementsByTagName('script')
    for (let i = 0; i < scripts.length; i++) {
      const script = scripts[i]
      const newScript = document.createElement('script')

      if (script.src) {
        newScript.src = script.src
      } else {
        newScript.textContent = script.textContent
      }

      document.body.appendChild(newScript)
    }
  }
}

// Klarna event handlers
const handleKlarnaEvent = (event: MessageEvent) => {
  // Listen for Klarna events (optional - for additional tracking)
  if (event.data && typeof event.data === 'object') {
    console.log('Klarna event:', event.data)
  }
}

onMounted(() => {
  fetchKlarnaData()
  window.addEventListener('message', handleKlarnaEvent)
})

onUnmounted(() => {
  window.removeEventListener('message', handleKlarnaEvent)
})
</script>

<template>
  <main class="bg-gray-50 min-h-screen">
    <div class="w-full px-4 py-4 md:py-6">
      <!-- Header -->
      <div class="mb-6 text-center">
        <h1 class="text-2xl font-bold text-gray-900">{{ t('klarnaCheckout.title') }}</h1>
        <p class="text-sm text-gray-600 mt-2">
          {{ t('klarnaCheckout.subtitle') }}
        </p>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="bg-white border-2 border-gray-300 rounded shadow-sm p-12">
        <div class="flex items-center justify-center">
          <div class="text-center">
            <div class="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
            <p class="text-gray-600 text-lg">{{ t('klarnaCheckout.loadingForm') }}</p>
          </div>
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="bg-red-50 border-2 border-red-300 rounded shadow-sm p-6">
        <div class="text-center">
          <svg class="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          <h2 class="text-xl font-semibold text-red-900 mb-2">{{ t('klarnaCheckout.errorTitle') }}</h2>
          <p class="text-red-700 mb-6">{{ error }}</p>
          <button
            @click="router.push(`/payment/${uuid}/${hash}`)"
            class="px-6 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            {{ t('klarnaCheckout.backToPayment') }}
          </button>
        </div>
      </div>

      <!-- Klarna Container -->
      <div v-else class="bg-white border-2 border-gray-300 rounded shadow-sm p-6">
        <!-- Klarna Payments API widget -->
        <div
          v-if="usePaymentsAPI"
          id="klarna-payments-container"
          style="min-height: 300px; width: 100%; border: 2px solid red; background: yellow;"
        ></div>

        <!-- Klarna Checkout API snippet -->
        <div
          v-else
          id="klarna-checkout-container"
          style="min-height: 300px; width: 100%;"
        ></div>

        <!-- Info text -->
        <div class="mt-6 text-center text-sm text-gray-600">
          <p>
            {{ t('klarnaCheckout.redirectInfo') }}
          </p>
        </div>
      </div>

      <!-- Security badges -->
      <div class="mt-8 text-center">
        <div class="inline-flex items-center gap-4 text-sm text-gray-600">
          <div class="flex items-center gap-2">
            <svg class="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd"/>
            </svg>
            <span>{{ t('klarnaCheckout.securePayment') }}</span>
          </div>
          <div class="flex items-center gap-2">
            <svg class="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
            </svg>
            <span>{{ t('klarnaCheckout.encryptedConnection') }}</span>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>
